import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher, useNavigationType } from 'react-router';
import type {
    TileViewModel,
    TileableModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import type { DirectoryEntityType } from '~/ui-rocco/pages/directory-browse/DirectoryBrowsePage';
import { LOAD_MORE_DIRECTORY_ACTION } from '~/routes/actions.load-more-directory';
import { useTracker } from '@archipro-website/tracker';

/**
 * Products per page for the legacy A/B/A products grid — 42 = 3 sections × 14.
 * Both the initial load and each "View More" click load this many.
 */
export const PRODUCTS_PER_PAGE = 42;

/**
 * Page size for the 5.0 uniform browse grid (DirectoryUniformGrid). 36 divides
 * evenly by 2, 3, 4 and 6 columns, so at those counts a page always ends on a
 * full row with an injected module landing on the boundary. The one exception
 * is the Display dropdown's 5-column option (36 % 5 = 1): its last row runs
 * ragged and injections land mid-page — accepted, since tiles reflow into one
 * continuous grid as pages load.
 *
 * It is a page of products AND the grid's cell budget: amplifier (AAA) cells
 * spend cells out of it rather than adding to it, so how many amplified
 * professionals a category has — three, one, or none — never changes the page
 * shape or the `?page=N` offsets. It is exactly one AMPLIFIER_PAGE_CELLS cycle.
 *
 * Only multiples of that cycle are worth setting here. While more products are
 * still to come the grid renders whole cycles only (DirectoryUniformGrid holds
 * the surplus back so the last row is never short), so 48 or 60 would fetch
 * more than 36 and still draw 36 — the extra is paid for and not shown.
 */
export const BROWSE_PRODUCTS_PER_PAGE = 36;

// ============================================================================
// Pagination Helper Functions
// ============================================================================

/**
 * Calculate total products loaded up to and including a given page.
 * Page 1: pageSize, Page 2: 2·pageSize, Page N: pageSize · N.
 */
export const getProductCountForPage = (
    page: number,
    pageSize: number = PRODUCTS_PER_PAGE,
): number => {
    if (page <= 0) return 0;
    return pageSize * page;
};

/**
 * Get section indices for a given page.
 * Every page has 3 sections (42 products = 3 × 14).
 * Page 1: sections [0, 1, 2]
 * Page 2: sections [3, 4, 5]
 * Page N: sections [3*(N-1), 3*(N-1)+1, 3*(N-1)+2]
 */
export const getSectionsForPage = (page: number): number[] => {
    const start = 3 * (page - 1);
    return [start, start + 1, start + 2];
};

/**
 * Get layout type for a section index.
 * Even indices = Layout A (featured on right)
 * Odd indices = Layout B (featured on left)
 */
export const getLayoutForSection = (sectionIndex: number): 'A' | 'B' => {
    return sectionIndex % 2 === 0 ? 'A' : 'B';
};

/**
 * Calculate maximum valid page number for a given total count.
 */
export const getMaxPage = (
    totalCount: number,
    pageSize: number = PRODUCTS_PER_PAGE,
): number => {
    if (totalCount <= 0) return 1;
    return Math.ceil(totalCount / pageSize);
};

interface UseProductViewMoreParams {
    /**
     * Initial products from the server-side loader.
     */
    initialProducts: TileViewModel<TileableModel>[];
    /**
     * Total number of products available in this category.
     */
    totalCount: number;
    /**
     * The category link path (e.g., "/products/kitchens").
     */
    categoryLink: string;
    /**
     * Optional: Active filters to apply when loading more products.
     */
    filters?: Record<string, unknown>;
    /**
     * Optional: the in-listing keyword term. Repeated on every later page so
     * View More stays inside the result set the first page came from.
     */
    search?: string;
    /**
     * Deep-linking: First page that has loaded products (1-indexed).
     * When provided, enables skeleton lazy-loading for pages below this.
     * Default: 1 (no skeletons, all products loaded from page 1)
     */
    firstLoadedPage?: number;
    /** Directory entity (drives the search index server-side). Default products. */
    entityType?: DirectoryEntityType;
    /** Tiles per page / View More step. Default {@link PRODUCTS_PER_PAGE} (42). */
    pageSize?: number;
    /**
     * Active sort from the URL (Sort dropdown), forwarded to the load-more
     * fetches so appended pages keep the same order as the initial grid.
     */
    sortBy?: string;
    /**
     * Absolute offset the initial grid started at. Added to every load-more
     * request so appended pages continue from where the server left off rather
     * than from zero. Non-zero only when the server entered the result set part
     * way in, as the curated products root does. Default 0.
     */
    offsetBase?: number;
    /**
     * Rewrite `?page=N` (and log DirectoryPageChange) when a load-more fetch
     * starts. Accurate for the button-only legacy grids, but ahead of the
     * viewport under infinite scroll — the revamp browse passes false and
     * DirectoryUniformGrid keeps the param in step with the page actually
     * being viewed instead (useViewportPageSync). Default true.
     */
    updateUrlOnLoad?: boolean;
}

interface UseProductViewMoreReturn {
    /**
     * All products currently loaded (skeleton pages + initial + View More).
     * Products are merged in correct order for grid rendering.
     */
    products: TileViewModel<TileableModel>[];
    /**
     * Whether there are more products to load (via View More).
     */
    hasMore: boolean;
    /**
     * Whether a View More fetch is currently in progress.
     */
    isLoading: boolean;
    /**
     * Error message if the last fetch failed, null otherwise.
     */
    error: string | null;
    /**
     * Function to trigger loading more products (View More button).
     */
    loadMore: () => void;
    /**
     * Function to load products for a skeleton page (lazy-loading).
     * Called when a skeleton section enters the viewport.
     */
    loadSkeletonPage: (pageNumber: number) => void;
    /**
     * Set of page numbers currently being loaded (for skeleton pages).
     */
    loadingPages: Set<number>;
    /**
     * Current first loaded page (updates when skeleton pages load).
     * Used by the grid to determine how many skeletons to render.
     */
    currentFirstLoadedPage: number;
    /**
     * Whether products were restored from cache (POP navigation).
     * Used to suppress deep-link scrollIntoView on back navigation.
     */
    isRestoredFromCache: boolean;
    /**
     * The `?page=N` value the next Load More / View More step lands on. Used to
     * render the Load More control as a real crawlable anchor (`<a href>`) so
     * bots can walk the item set without executing JS.
     */
    nextPage: number;
}

// Module-level cache for product state — persists across navigations within the same tab.
// On POP (back/forward) navigation, the exact products the user saw are restored,
// preserving DOM height so scroll position restoration works correctly.
const viewMoreCache = new Map<
    string,
    {
        products: TileViewModel<TileableModel>[];
        serverHasMore: boolean;
    }
>();

interface LoadMoreResponse {
    tiles: TileViewModel<TileableModel>[];
    hasMore: boolean;
    totalCount: number;
}

/**
 * Hook for managing "View More" product loading functionality.
 *
 * Features:
 * - Client-side pagination using useFetcher (no URL changes)
 * - Appends new products to existing list
 * - Handles race conditions with AbortController
 * - Resets state when category/filters change
 * - Provides loading and error states
 *
 * @example
 * ```tsx
 * const { products, hasMore, isLoading, error, loadMore } = useProductViewMore({
 *   initialProducts: loaderProducts,
 *   totalCount: pagination.totalCount,
 *   categoryLink: '/products/kitchens',
 * });
 * ```
 */
export function useDirectoryViewMore({
    initialProducts,
    totalCount,
    categoryLink,
    filters,
    search,
    firstLoadedPage = 1,
    entityType = 'products',
    pageSize = PRODUCTS_PER_PAGE,
    sortBy,
    offsetBase = 0,
    updateUrlOnLoad = true,
}: UseProductViewMoreParams): UseProductViewMoreReturn {
    const fetcher = useFetcher<LoadMoreResponse>();
    const navigationType = useNavigationType();
    const tracker = useTracker();

    // Page-size-aware offset helpers (the exported product ones are fixed at 42).
    const tileCountForPage = (page: number): number =>
        page <= 0 ? 0 : pageSize * page;
    const pageFromTileCount = (count: number): number =>
        count <= 0 ? 1 : Math.ceil(count / pageSize);

    // On POP navigation, restore cached product state to preserve exact products
    // the user saw before navigating away. This ensures DOM height is preserved
    // so scroll position restoration works correctly.
    const cachedState =
        navigationType === 'POP' ? viewMoreCache.get(categoryLink) : undefined;
    const isRestoredFromCache = !!cachedState;

    // Pool of 3 fetchers for parallel skeleton loading
    const skeletonFetcher0 = useFetcher<LoadMoreResponse>();
    const skeletonFetcher1 = useFetcher<LoadMoreResponse>();
    const skeletonFetcher2 = useFetcher<LoadMoreResponse>();
    // Memoize the array to prevent dependency changes on every render
    const skeletonFetchers = useMemo(
        () => [skeletonFetcher0, skeletonFetcher1, skeletonFetcher2],
        [skeletonFetcher0, skeletonFetcher1, skeletonFetcher2],
    );

    // State for accumulated products (server-loaded + View More loaded)
    // On POP, restore from cache; otherwise use loader's initialProducts
    const [products, setProducts] = useState<TileViewModel<TileableModel>[]>(
        cachedState?.products ?? initialProducts,
    );
    const [error, setError] = useState<string | null>(null);

    // Tracks whether a View More fetch is pending AND products haven't been appended yet.
    // Prevents the gap render where fetcher.state='idle' (skeleton removed) but
    // setProducts() hasn't fired yet (products not appended).
    const [isViewMoreLoading, setIsViewMoreLoading] = useState(false);

    // Track hasMore from server response (more reliable than calculating from totalCount)
    const [serverHasMore, setServerHasMore] = useState(() => {
        if (cachedState?.serverHasMore !== undefined) {
            return cachedState.serverHasMore;
        }
        // Account for deep-link offset: products start at firstLoadedPage, not page 1.
        // Without this, deep-linking to the last page (e.g., ?page=16) would show
        // hasMore=true because 126 < 672, when in reality pages 14-16 are fully loaded.
        // `offsetBase` shifts where the server started, so it counts towards
        // the pool consumed — otherwise a curated block near the end of the
        // pool keeps offering View More with nothing left to fetch.
        const serverEndOffset =
            offsetBase +
            tileCountForPage(firstLoadedPage - 1) +
            initialProducts.length;
        return serverEndOffset < totalCount;
    });

    // Fetch identity counter: incremented each time loadMore() initiates a fetch.
    // This replaces the previous hasFetchedRef two-effect pattern which was fragile
    // under React 18's startTransition (used by Remix's router), where the 'loading'
    // state could be skipped entirely, preventing the ref from being reset.
    const fetchCountRef = useRef(0);
    // Tracks which fetch has been processed. Set to fetchCountRef.current after
    // processing, and also on filter/category resets to invalidate in-flight fetches.
    const processedFetchCountRef = useRef(0);

    // Track loaded product IDs to prevent duplicates when batch sizes don't align
    // (e.g., initial load returns 40 products, View More requests 14 at a time)
    // On POP cache restore, initialize from cached products (which include former
    // skeleton pages) to prevent dedup failures on subsequent View More clicks.
    const loadedIdsRef = useRef<Set<number>>(
        new Set(
            (cachedState?.products ?? initialProducts).map((p) => p.model.id),
        ),
    );

    // Ref to track the current category/filters/search/sort for detecting changes
    const categoryRef = useRef(categoryLink);
    const filtersRef = useRef(filters);
    const searchRef = useRef(search);
    const sortRef = useRef(sortBy);

    // =========================================================================
    // Skeleton Lazy-Loading State
    // =========================================================================

    // Store loaded skeleton page products (Map: pageNumber -> products)
    const [skeletonProducts, setSkeletonProducts] = useState<
        Map<number, TileViewModel<TileableModel>[]>
    >(new Map());

    // Track which skeleton pages are currently loading
    const [loadingPages, setLoadingPages] = useState<Set<number>>(new Set());

    // Track the current first loaded page (updates when skeleton pages load)
    // When restored from cache, all products are present → no skeletons needed
    const [currentFirstLoadedPage, setCurrentFirstLoadedPage] = useState(
        isRestoredFromCache ? 1 : firstLoadedPage,
    );

    // Track which fetcher is loading which page (fetcher index -> page number)
    const fetcherPageMapRef = useRef<Map<number, number>>(new Map());

    // Queue for overflow requests when all fetchers are busy
    const pendingQueueRef = useRef<number[]>([]);

    // Track which skeleton fetchers have been processed (to avoid double-processing)
    const processedFetchersRef = useRef<Set<number>>(new Set());

    // Track which skeleton pages have completed loading (for contiguous calculation).
    // currentFirstLoadedPage only decreases when pages form a contiguous block
    // downward from the original firstLoadedPage, preventing layout shifts when
    // parallel skeleton fetchers complete out of order.
    const loadedSkeletonPagesRef = useRef<Set<number>>(new Set());

    // Track if this is the initial mount (to skip reset effects on first render)
    const isInitialMountRef = useRef(true);

    // Track previous initialProducts reference to detect actual navigation changes
    const prevInitialProductsRef = useRef<
        TileViewModel<TileableModel>[] | null
    >(null);

    // Tracks the effective first page of the products array for offset calculation.
    // After POP cache restore, products start from page 1 (includes former skeleton pages),
    // but firstLoadedPage from the loader may be higher (e.g., 5 for ?page=7).
    // Using firstLoadedPage directly would double-count skeleton products in the offset.
    const effectiveFirstPageRef = useRef(
        isRestoredFromCache ? 1 : firstLoadedPage,
    );

    // Use server's hasMore state (updated from action responses)
    const hasMore = serverHasMore;
    // isLoading stays true until BOTH: fetcher is idle AND products have been appended.
    // This eliminates the gap render where the skeleton disappears before new sections appear.
    const isLoading = isViewMoreLoading || fetcher.state === 'loading';

    // Reset state when category, filters, search or sort change
    useEffect(() => {
        const categoryChanged = categoryRef.current !== categoryLink;
        const filtersChanged =
            JSON.stringify(filtersRef.current) !== JSON.stringify(filters);
        // A new term is a new result set: the pages already loaded came from
        // the old one, so keeping them would mix two searches in one grid.
        const searchChanged = searchRef.current !== search;
        const sortChanged = sortRef.current !== sortBy;

        if (categoryChanged || filtersChanged || searchChanged || sortChanged) {
            // Clear stale cache entry when navigating to a different category
            if (categoryChanged) {
                viewMoreCache.delete(categoryRef.current);
            }

            // Update refs
            categoryRef.current = categoryLink;
            filtersRef.current = filters;
            searchRef.current = search;
            sortRef.current = sortBy;

            // Reset state
            setProducts(initialProducts);
            setError(null);
            setIsViewMoreLoading(false);
            // Reset hasMore based on new initial state (offset-aware for deep-links)
            setServerHasMore(
                offsetBase +
                    tileCountForPage(firstLoadedPage - 1) +
                    initialProducts.length <
                    totalCount,
            );
            // Mark any in-flight fetch as stale so its response is ignored
            processedFetchCountRef.current = fetchCountRef.current;

            // Reset loaded IDs with new initial products
            loadedIdsRef.current = new Set(
                initialProducts.map((p) => p.model.id),
            );

            // Reset skeleton state
            setSkeletonProducts(new Map());
            setLoadingPages(new Set());
            setCurrentFirstLoadedPage(firstLoadedPage);
            effectiveFirstPageRef.current = firstLoadedPage;
            fetcherPageMapRef.current.clear();
            pendingQueueRef.current = [];
            processedFetchersRef.current.clear();
            loadedSkeletonPagesRef.current.clear();
        }
    }, [
        categoryLink,
        filters,
        search,
        sortBy,
        initialProducts,
        totalCount,
        firstLoadedPage,
    ]);

    // Handle fetcher data when it arrives.
    // Uses a fetch counter instead of the previous hasFetchedRef pattern.
    // This is safe under React 18's startTransition because it doesn't depend
    // on observing the 'loading' state — it only checks whether there's an
    // unprocessed fetch (processedFetchCount < fetchCount).
    useEffect(() => {
        if (
            fetcher.state === 'idle' &&
            fetcher.data &&
            processedFetchCountRef.current < fetchCountRef.current
        ) {
            processedFetchCountRef.current = fetchCountRef.current;

            const response = fetcher.data;

            if (response.tiles && response.tiles.length > 0) {
                // Cast tiles to avoid JsonifyObject type incompatibility
                const newProducts =
                    response.tiles as unknown as TileViewModel<TileableModel>[];

                // Filter out duplicates (can occur when batch sizes don't align)
                // Log warnings to help surface offset calculation bugs
                const uniqueNewProducts = newProducts.filter((p) => {
                    if (loadedIdsRef.current.has(p.model.id)) {
                        if (process.env.NODE_ENV === 'development') {
                            console.warn(
                                '[useProductViewMore] Duplicate product detected:',
                                {
                                    id: p.model.id,
                                    totalLoaded: loadedIdsRef.current.size,
                                },
                            );
                        }
                        return false; // Skip duplicate
                    }
                    loadedIdsRef.current.add(p.model.id);
                    return true;
                });

                if (uniqueNewProducts.length > 0) {
                    setProducts((prev) => [...prev, ...uniqueNewProducts]);
                }
                setError(null);

                // Update hasMore from server response
                if (response.hasMore !== undefined) {
                    setServerHasMore(response.hasMore);
                }
            } else if (response.tiles && response.tiles.length === 0) {
                // No more products, but not an error
                setError(null);
                // Server returned empty array, no more products to load
                setServerHasMore(false);
            }

            // Clear View More loading AFTER products are set (same React batch).
            // Both setProducts() and setIsViewMoreLoading(false) are in the same
            // synchronous useEffect body, so React batches them into one render —
            // the skeleton disappears in the same frame as new sections appear.
            setIsViewMoreLoading(false);
        }
    }, [fetcher.state, fetcher.data]);

    // Reset products when initialProducts actually changes (not on initial mount)
    // This prevents wiping skeleton loading state during initial render
    useEffect(() => {
        // Skip initial mount - just store the reference
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            prevInitialProductsRef.current = initialProducts;
            return;
        }

        // Only reset if initialProducts reference actually changed (navigation occurred)
        if (prevInitialProductsRef.current !== initialProducts) {
            prevInitialProductsRef.current = initialProducts;

            setProducts(initialProducts);
            setError(null);
            setIsViewMoreLoading(false);
            // Reset hasMore based on new initial state (offset-aware for deep-links)
            setServerHasMore(
                offsetBase +
                    tileCountForPage(firstLoadedPage - 1) +
                    initialProducts.length <
                    totalCount,
            );
            // Reset loaded IDs with new initial products
            loadedIdsRef.current = new Set(
                initialProducts.map((p) => p.model.id),
            );

            // Reset skeleton state for new navigation
            setSkeletonProducts(new Map());
            setLoadingPages(new Set());
            setCurrentFirstLoadedPage(firstLoadedPage);
            effectiveFirstPageRef.current = firstLoadedPage;
            fetcherPageMapRef.current.clear();
            pendingQueueRef.current = [];
            processedFetchersRef.current.clear();
            loadedSkeletonPagesRef.current.clear();
        }
    }, [initialProducts, totalCount, firstLoadedPage]);

    // =========================================================================
    // Skeleton Fetcher Pool Response Handler
    // =========================================================================

    // Helper to process a completed fetcher response
    const processSkeletonFetcherResponse = useCallback(
        (fetcherIndex: number, response: LoadMoreResponse) => {
            const pageNumber = fetcherPageMapRef.current.get(fetcherIndex);
            if (pageNumber === undefined) return;

            // Clean up the fetcher mapping
            fetcherPageMapRef.current.delete(fetcherIndex);

            if (response.tiles && response.tiles.length > 0) {
                // Cast tiles to avoid JsonifyObject type incompatibility
                const newProducts =
                    response.tiles as unknown as TileViewModel<TileableModel>[];

                // Filter out duplicates and add to loaded IDs
                // Log warnings to help surface offset calculation bugs
                const uniqueProducts = newProducts.filter((p) => {
                    if (loadedIdsRef.current.has(p.model.id)) {
                        if (process.env.NODE_ENV === 'development') {
                            console.warn(
                                '[useProductViewMore] Duplicate product in skeleton page:',
                                {
                                    id: p.model.id,
                                    pageNumber,
                                    totalLoaded: loadedIdsRef.current.size,
                                },
                            );
                        }
                        return false;
                    }
                    loadedIdsRef.current.add(p.model.id);
                    return true;
                });

                if (uniqueProducts.length > 0) {
                    // Store skeleton products for this page
                    setSkeletonProducts((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(pageNumber, uniqueProducts);
                        return newMap;
                    });

                    // Update currentFirstLoadedPage only when pages form a contiguous
                    // block downward from the current value. This prevents layout shifts
                    // when parallel fetchers complete out of order (e.g., page 1 before 2-3).
                    loadedSkeletonPagesRef.current.add(pageNumber);
                    setCurrentFirstLoadedPage((prev) => {
                        let newFirst = prev;
                        while (
                            newFirst > 1 &&
                            loadedSkeletonPagesRef.current.has(newFirst - 1)
                        ) {
                            newFirst--;
                        }
                        return newFirst;
                    });
                }
            }

            // Remove from loading set
            setLoadingPages((prev) => {
                const newSet = new Set(prev);
                newSet.delete(pageNumber);
                return newSet;
            });
        },
        [],
    );

    // Process pending queue when a fetcher becomes available
    // Uses functional state update to avoid stale closure issues
    const processQueue = useCallback(() => {
        if (pendingQueueRef.current.length === 0) return;

        // Find an idle fetcher that isn't already assigned
        const idleFetcherIndex = skeletonFetchers.findIndex(
            (f, idx) =>
                f.state === 'idle' && !fetcherPageMapRef.current.has(idx),
        );

        if (idleFetcherIndex === -1) return;

        const nextPage = pendingQueueRef.current.shift();
        if (nextPage === undefined) return;

        // Use functional state update to check current state and avoid stale closure
        setLoadingPages((currentLoadingPages) => {
            // Check if page is already loading
            if (currentLoadingPages.has(nextPage)) {
                return currentLoadingPages;
            }

            // Check if page is already loaded (use ref to get current skeleton products)
            // Note: skeletonProducts state check moved to the guard in loadSkeletonPage
            // The fetcher assignment happens synchronously, so this is safe

            // Assign fetcher to this page
            fetcherPageMapRef.current.set(idleFetcherIndex, nextPage);

            // Calculate offset and limit (all pages are 42 products)
            const offset = tileCountForPage(nextPage - 1);
            const limit = pageSize;

            const params = new URLSearchParams({
                entityType,
                categoryLink,
                offset: offset.toString(),
                limit: limit.toString(),
            });

            if (filters && Object.keys(filters).length > 0) {
                params.set('filters', JSON.stringify(filters));
            }

            if (search) {
                params.set('search', search);
            }

            if (sortBy) {
                params.set('sort', sortBy);
            }

            const url = `${LOAD_MORE_DIRECTORY_ACTION}?${params.toString()}`;
            const targetFetcher = skeletonFetchers[idleFetcherIndex];
            if (targetFetcher) {
                targetFetcher.load(url);
            }

            // Return updated loading pages set
            return new Set([...currentLoadingPages, nextPage]);
        });
    }, [skeletonFetchers, categoryLink, filters, search, sortBy]);

    // Consolidated effect for all skeleton fetchers
    // This prevents race conditions from having separate effects for each fetcher
    useEffect(() => {
        // Process each fetcher in order
        const fetcherDataArray = [
            { fetcher: skeletonFetcher0, index: 0 },
            { fetcher: skeletonFetcher1, index: 1 },
            { fetcher: skeletonFetcher2, index: 2 },
        ];

        for (const { fetcher, index } of fetcherDataArray) {
            // Handle completed fetcher response
            if (
                fetcher.state === 'idle' &&
                fetcher.data &&
                fetcherPageMapRef.current.has(index) &&
                !processedFetchersRef.current.has(index)
            ) {
                processedFetchersRef.current.add(index);
                // Cast to avoid JsonifyObject type issue
                processSkeletonFetcherResponse(
                    index,
                    fetcher.data as unknown as LoadMoreResponse,
                );
                processQueue();
            }

            // Reset processed flag when fetcher starts loading
            if (fetcher.state === 'loading') {
                processedFetchersRef.current.delete(index);
            }
        }
    }, [
        skeletonFetcher0,
        skeletonFetcher1,
        skeletonFetcher2,
        processSkeletonFetcherResponse,
        processQueue,
    ]);

    // =========================================================================
    // Load Skeleton Page Function
    // =========================================================================

    // Implementation that captures current state — identity changes on every
    // state update, which is fine because it's only used via the ref below.
    const loadSkeletonPageImpl = useCallback(
        (pageNumber: number) => {
            // Don't load if:
            // - Already loading this page
            // - Already loaded this page
            // - Page is >= currentFirstLoadedPage (not a skeleton)
            if (
                loadingPages.has(pageNumber) ||
                skeletonProducts.has(pageNumber) ||
                pageNumber >= currentFirstLoadedPage
            ) {
                return;
            }

            // Find an idle fetcher that isn't already assigned to a page
            const idleFetcherIndex = skeletonFetchers.findIndex(
                (f, idx) =>
                    f.state === 'idle' && !fetcherPageMapRef.current.has(idx),
            );

            if (idleFetcherIndex === -1) {
                // All fetchers busy - queue the request if not already queued
                if (!pendingQueueRef.current.includes(pageNumber)) {
                    pendingQueueRef.current.push(pageNumber);
                }
                return;
            }

            // Mark as loading
            setLoadingPages((prev) => new Set([...prev, pageNumber]));
            fetcherPageMapRef.current.set(idleFetcherIndex, pageNumber);

            // Calculate offset and limit for this page (all pages are 42 products)
            const offset = tileCountForPage(pageNumber - 1);
            const limit = pageSize;

            // Build request
            const params = new URLSearchParams({
                entityType,
                categoryLink,
                offset: offset.toString(),
                limit: limit.toString(),
            });

            // Add filters if present
            if (filters && Object.keys(filters).length > 0) {
                params.set('filters', JSON.stringify(filters));
            }

            if (search) {
                params.set('search', search);
            }

            if (sortBy) {
                params.set('sort', sortBy);
            }

            const url = `${LOAD_MORE_DIRECTORY_ACTION}?${params.toString()}`;
            const targetFetcher = skeletonFetchers[idleFetcherIndex];
            if (targetFetcher) {
                targetFetcher.load(url);
            }
        },
        [
            loadingPages,
            skeletonProducts,
            currentFirstLoadedPage,
            categoryLink,
            filters,
            search,
            skeletonFetchers,
            sortBy,
        ],
    );

    // Stable ref pattern: the ref always points to the latest implementation,
    // while the wrapper callback has a stable identity (empty deps). This prevents
    // React.memo'd DirectorySkeletonSection from getting stale closures — its
    // IntersectionObserver always delegates to the current implementation.
    const loadSkeletonPageRef = useRef(loadSkeletonPageImpl);
    loadSkeletonPageRef.current = loadSkeletonPageImpl;

    const loadSkeletonPage = useCallback(
        (pageNumber: number) => loadSkeletonPageRef.current(pageNumber),
        [],
    );

    // =========================================================================
    // Merge Skeleton Products with Server/View More Products
    // =========================================================================

    // Combine skeleton products + server-loaded products + view more products
    // Products are merged in page order for correct grid rendering.
    // Only includes skeleton pages that form a contiguous block between
    // currentFirstLoadedPage and firstLoadedPage. Pages loaded out of order
    // wait in skeletonProducts until the gap is filled, preventing layout shifts.
    const allProducts = useMemo(() => {
        if (skeletonProducts.size === 0) {
            return products;
        }

        // Only include skeleton pages contiguous with server-loaded pages
        const skeletonPageNumbers = Array.from(skeletonProducts.keys())
            .filter((p) => p >= currentFirstLoadedPage && p < firstLoadedPage)
            .sort((a, b) => a - b);

        if (skeletonPageNumbers.length === 0) {
            return products;
        }

        const skeletonProductsList: TileViewModel<TileableModel>[] = [];
        for (const pageNum of skeletonPageNumbers) {
            const pageProducts = skeletonProducts.get(pageNum);
            if (pageProducts) {
                skeletonProductsList.push(...pageProducts);
            }
        }

        return [...skeletonProductsList, ...products];
    }, [skeletonProducts, products, currentFirstLoadedPage, firstLoadedPage]);

    const loadMore = useCallback(() => {
        // Don't load if already loading or no more products
        if (isLoading || !hasMore) {
            return;
        }

        // Clear any previous error
        setError(null);

        // Calculate the server's starting offset (where products begin in the catalog).
        // Uses effectiveFirstPageRef instead of firstLoadedPage because after POP cache
        // restore, products include former skeleton pages (start from page 1), but
        // firstLoadedPage from the loader still reflects the deep-link page.
        // `offsetBase` shifts this when the server entered the result set part
        // way in, so the next page continues from there rather than repeating
        // products already on screen.
        const serverStartOffset =
            offsetBase +
            (effectiveFirstPageRef.current > 1
                ? tileCountForPage(effectiveFirstPageRef.current - 1)
                : 0);

        // The offset for View More is where products started + products loaded so far
        const offset = serverStartOffset + products.length;

        // Each View More loads 3 sections (42 products)
        // The last fetch may get fewer if not enough products remain
        const remaining = totalCount - offset;
        const limit = Math.min(pageSize, remaining);

        // Build the URL with query parameters
        const params = new URLSearchParams({
            entityType,
            categoryLink,
            offset: offset.toString(),
            limit: limit.toString(),
        });

        // Add filters if present
        if (filters && Object.keys(filters).length > 0) {
            params.set('filters', JSON.stringify(filters));
        }

        // Repeat the keyword term, as the skeleton loaders above do. Without
        // it a later page comes from the unfiltered set and lands in the same
        // grid as the first, mixing two result sets under one heading.
        if (search) {
            params.set('search', search);
        }

        if (sortBy) {
            params.set('sort', sortBy);
        }

        const url = `${LOAD_MORE_DIRECTORY_ACTION}?${params.toString()}`;

        // Increment fetch counter so the response effect knows there's a pending fetch
        fetchCountRef.current++;

        // Mark View More as loading BEFORE fetcher.load() so the skeleton stays visible
        // until products are actually appended (see setIsViewMoreLoading(false) in data effect)
        setIsViewMoreLoading(true);

        // Use fetcher.load for GET requests
        fetcher.load(url);

        // Update URL with page parameter (using replaceState to avoid history entries)
        // Calculate the page number after loading the new products. Excludes
        // `offsetBase` so the page number counts what the user has seen rather
        // than the absolute position in the result set.
        // Skipped when the grid owns the param instead (useViewportPageSync):
        // under infinite scroll a request-time rewrite runs ahead of the
        // viewport and can skip page numbers when loads fire back-to-back.
        if (updateUrlOnLoad) {
            const newProductCount = offset - offsetBase + limit;
            const newPage = pageFromTileCount(newProductCount);

            // SSR guard + idempotent check: only update if in browser and page changed
            if (typeof window !== 'undefined' && newPage > 1) {
                const currentUrl = new URL(window.location.href);
                const currentPageParam = currentUrl.searchParams.get('page');

                // Only update if page parameter actually changed
                if (currentPageParam !== newPage.toString()) {
                    currentUrl.searchParams.set('page', newPage.toString());
                    window.history.replaceState({}, '', currentUrl.toString());
                }
            }

            // Track page change for analytics
            tracker.log('DirectoryPageChange', {
                url: new URL(window.location.href),
                targetTracker: 'archiproTracker',
                data: {
                    page: newPage,
                    category: categoryLink,
                },
            });
        }
    }, [
        isLoading,
        hasMore,
        categoryLink,
        products.length,
        totalCount,
        filters,
        search,
        fetcher,
        tracker,
        sortBy,
        offsetBase,
        updateUrlOnLoad,
    ]);

    // Persist product state to cache for POP navigation restoration.
    // Stores allProducts (skeleton + server + View More) instead of just products,
    // so deep-link POP restores the full product set including former skeleton pages.
    useEffect(() => {
        viewMoreCache.set(categoryLink, {
            products: allProducts,
            serverHasMore,
        });
    }, [categoryLink, allProducts, serverHasMore]);

    // The `?page=N` a Load More / View More click will land on — mirrors the
    // offset maths in loadMore so the crawlable anchor points at the real next
    // page. Recomputed as products load, but only the pre-interaction value
    // (page 1 → 2) is seen by crawlers and the SSR HTML.
    const nextPageServerStartOffset =
        effectiveFirstPageRef.current > 1
            ? tileCountForPage(effectiveFirstPageRef.current - 1)
            : 0;
    const nextPageOffset = nextPageServerStartOffset + products.length;
    const nextPageLimit = Math.max(
        0,
        Math.min(pageSize, totalCount - nextPageOffset),
    );
    const nextPage = pageFromTileCount(nextPageOffset + nextPageLimit);

    return {
        // Return merged products (skeleton + server + View More) for grid rendering
        products: allProducts,
        hasMore,
        isLoading,
        error,
        loadMore,
        // Skeleton lazy-loading
        loadSkeletonPage,
        loadingPages,
        currentFirstLoadedPage,
        isRestoredFromCache,
        nextPage,
    };
}

/**
 * Products-specific alias of {@link useDirectoryViewMore} (entityType defaults to
 * `products`, pageSize to 42). Kept so the products grid reads naturally.
 */
export const useProductViewMore = useDirectoryViewMore;
